// pages/MyCoursesPage.jsx
// Every course the signed in user owns. Route: /my-courses
// Data is the same coursesApi.getAll() the catalog uses, and ownership is the
// hasAccess flag that response already carries, so this adds no new API call.

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isLoggedIn } from "../api/auth";
import { coursesApi } from "../api/courses";
import { workbooksApi } from "../api/workbooks";
import Navbar from "../components/Navbar";
import OwnedCourseCard, { OWNED_CARD_CSS } from "../components/course/OwnedCourseCard";
import WorkbookTrackCard, { WORKBOOK_TRACK_CARD_CSS } from "../components/WorkbookTrackCard";
import { useWorkbookBundles } from "../data/workbookBundles";
import { catalogueApi } from "../api/catalogue";
import { loginUrlFor } from "../lib/nextUrl";

const C = {
  cream: "#f2efe8",
  ink: "#1c1a17",
  muted: "#8a8478",
  blue: "#2563eb",
};

const ACCENT_HEX = {
  blue: "#2563eb",
  red: "#dc2626",
  amber: "#d97706",
  green: "#16a34a",
};

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const loggedIn = isLoggedIn();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  // Workbook ownership rides the /api/workbooks response (workbooks, userProduct,
  // bundleStats). Kept separate from courses so one failing fetch does not blank
  // the other section.
  const [wbResponse, setWbResponse] = useState(null);
  // Bundle copy comes from the catalogue, ownership from the workbooks
  // response. Kept as two fetches so one failing does not blank the other.
  const [catalogue, setCatalogue] = useState([]);
  const [wbLoading, setWbLoading] = useState(true);

  useEffect(() => {
    if (!loggedIn) {
      // replace + ?next=: a state-driven redirect that back must not bounce off,
      // and signing in returns here rather than to the course catalogue.
      navigate(loginUrlFor("/my-courses"), { replace: true });
      return;
    }
    let mounted = true;
    coursesApi
      .getAll()
      .then((list) => {
        if (!mounted) return;
        setCourses((list ?? []).filter((c) => c.hasAccess === true));
        setLoading(false);
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });
    Promise.all([
      workbooksApi.getAll().catch(() => null),
      catalogueApi.getBundles().catch(() => null),
    ])
      .then(([res, bundles]) => {
        if (!mounted) return;
        setWbResponse(res ?? null);
        setCatalogue(bundles ?? []);
        setWbLoading(false);
      })
      .catch(() => {
        if (mounted) setWbLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [loggedIn, navigate]);

  // Same derivation the Workbooks page uses; we only render the owned tracks.
  const bundles = useWorkbookBundles({
    bundles: catalogue,
    workbooks: wbResponse?.workbooks,
    ownedBundleSlugs: wbResponse?.ownedBundleSlugs,
    bundleStats: wbResponse?.bundleStats,
  });
  const ownedBundles = bundles.filter((b) => b.owned);

  if (!loggedIn) return null;

  return (
    <div style={{ background: C.cream, minHeight: "100vh" }}>
      <Navbar />
      <div className="lib-wrap">
        <div style={{ borderTop: `2px solid ${C.ink}`, paddingTop: 28, marginBottom: 32 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>
            Courses you own
          </div>
          <h1 style={{ margin: 0, fontFamily: "'Playfair Display', Georgia, serif", fontSize: 44, fontWeight: 700, lineHeight: 1.1, color: C.ink }}>
            My Courses
          </h1>
        </div>

        {!loading && courses.length === 0 && (
          <div style={{ fontSize: 15, fontWeight: 300, lineHeight: 1.8, color: C.muted }}>
            You do not own any courses yet.{" "}
            <Link
              to="/courses"
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color: C.blue, letterSpacing: "0.04em", textDecoration: "none" }}
            >
              Browse tracks →
            </Link>
          </div>
        )}

        {courses.length > 0 && (
          <div className="lib-grid">
            {courses.map((course) => (
              <OwnedCourseCard
                key={course.slug}
                accent={ACCENT_HEX[course.accent] || ACCENT_HEX.blue}
                badge={course.badge}
                title={course.title}
                desc={course.description}
                meta={`${course.moduleCount} modules · ${course.lessonCount} lessons`}
                ctaColor={course.accent === "red" ? ACCENT_HEX.red : ACCENT_HEX.blue}
                onContinue={() => navigate(`/course/${course.slug}`)}
              />
            ))}
          </div>
        )}

        {/* My workbooks: owned workbook tracks, rendered with the same card as
            the Workbooks page. The free track is always owned, so the empty
            state below is a safe fallback that normally does not appear. */}
        <div style={{ borderTop: `2px solid ${C.ink}`, paddingTop: 28, marginTop: 56, marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontFamily: "'Fraunces', Georgia, serif", fontSize: 34, fontWeight: 700, lineHeight: 1.1, color: C.ink }}>
            My workbooks
          </h2>
          <div style={{ marginTop: 10, fontSize: 14, fontWeight: 300, color: C.muted }}>
            Tracks you own
          </div>
        </div>

        {!wbLoading && ownedBundles.length === 0 && (
          <div style={{ fontSize: 15, fontWeight: 300, lineHeight: 1.8, color: C.muted }}>
            You do not own any workbook tracks yet.{" "}
            <Link
              to="/workbooks"
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color: C.blue, letterSpacing: "0.04em", textDecoration: "none" }}
            >
              Browse workbooks →
            </Link>
          </div>
        )}

        {!wbLoading && ownedBundles.length > 0 && (
          <div className="lib-wb-grid">
            {ownedBundles.map((b) => (
              <WorkbookTrackCard
                key={b.track}
                bundle={b}
                onOpen={() => navigate(`/workbooks/bundle/${b.track}/library`)}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        .lib-wrap{max-width:1160px;margin:0 auto;padding:56px 60px 64px;font-family:'DM Sans',system-ui,sans-serif;}
        .lib-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
        .lib-wb-grid{display:grid;grid-template-columns:1fr 1fr;gap:22px;}
        ${OWNED_CARD_CSS}
        ${WORKBOOK_TRACK_CARD_CSS}
        @media (max-width:760px){
          .lib-wrap{padding:40px 20px 56px;}
          .lib-grid{grid-template-columns:1fr;}
          .lib-wb-grid{grid-template-columns:1fr;}
        }
      `}</style>
    </div>
  );
}
